// Team tag catalog (/v1/teams/{id}/tags): the named, colored labels a team
// defines once and attaches to any of its workspaces. A tag belongs to exactly
// one team and a workspace may carry only its own team's tags, which is why
// there is no global listing — the caller fetches one catalog per team.
//
// Reading is any member role; creating is member+; renaming, recoloring and
// deleting are maintainer+, because those three reach every workspace on the
// team at once. Attaching and detaching is a workspace write — see
// setWorkspaceTags in api/workspaces.ts.

import { getJSON, sendJSON, request } from "./http.js";
import type { BnTagColor } from "../components/bn-tag.js";

const BASE = "/v1/teams";

/**
 * TagColor is the closed color enum the server validates. It aliases the bn-tag
 * palette — a type-only import, erased at build — so the wire values and the
 * rendered swatches cannot drift apart.
 */
export type TagColor = BnTagColor;

export interface Tag {
  id: string;
  name: string;
  color: TagColor;
}

/** TagMatch selects the semantics of a multi-tag filter; "all" is the default. */
export type TagMatch = "all" | "any";

/** listTags returns a team's tag catalog, name-sorted (any member role). */
export async function listTags(teamId: string): Promise<Tag[]> {
  const { items } = await getJSON<{ items: Tag[] }>(`${BASE}/${encodeURIComponent(teamId)}/tags`);
  return items;
}

/**
 * createTag adds a tag to the team catalog (member+). A name the team already
 * holds — ignoring case — answers tag_name_taken.
 */
export function createTag(teamId: string, name: string, color: TagColor): Promise<Tag> {
  return sendJSON<Tag>(`${BASE}/${encodeURIComponent(teamId)}/tags`, "POST", { name, color });
}

/**
 * updateTag renames and/or recolors a catalog tag (maintainer+). Workspaces link
 * by id, so every workspace carrying it relabels on its next load.
 */
export function updateTag(
  teamId: string,
  tagId: string,
  patch: { name?: string; color?: TagColor },
): Promise<Tag> {
  return sendJSON<Tag>(tagPath(teamId, tagId), "PATCH", patch);
}

/**
 * deleteTag removes the catalog entry and detaches it from every workspace on
 * the team (maintainer+). The workspaces themselves are untouched.
 */
export async function deleteTag(teamId: string, tagId: string): Promise<void> {
  await request(tagPath(teamId, tagId), { method: "DELETE" });
}

function tagPath(teamId: string, tagId: string): string {
  return `${BASE}/${encodeURIComponent(teamId)}/tags/${encodeURIComponent(tagId)}`;
}
